'use client'

import { useLocale, useTranslations } from 'next-intl'

import { useConfig } from '@/hooks/use-config'
import { Footer } from '@/components/footer'
import { buildFooterLegalLinks, buildFooterSections } from '@/components/footer-config'

/**
 * Wires the localized footer navigation and runtime build metadata (app version,
 * deployment region, and build id) from the client config into the presentational
 * Footer, which stays config- and locale-agnostic so it remains portable as a shadcn
 * registry block. The build string renders as hidden text in the footer, revealed only
 * on selection — a diagnostic aid. The build id mirrors ui-react's footer:
 * `<build_number>-<git_commit>`.
 */
interface FooterContainerProps {
    /**
     * Resolved on the server. Reading the clock here instead would be evaluated once
     * into the served HTML and again on hydration, and the two disagree across New Year
     * (`invariants.md#ssr-safe-rendered-dates`).
     */
    copyrightYear: string
}

export const FooterContainer = ({ copyrightYear }: FooterContainerProps) => {
    const config = useConfig()
    const locale = useLocale()
    const t = useTranslations('footer')

    return (
        <Footer
            sections={buildFooterSections(locale, t)}
            description={t('description')}
            copyright={t('copyright', { year: copyrightYear })}
            legalLinks={buildFooterLegalLinks(t)}
            region={config.region}
            build={{
                version: config.service.version,
                number: `${config.build.number}-${config.git.commit.short}`,
            }}
            source={config.service.name}
        />
    )
}
